import React from "react";
import { FaTruck, FaMotorcycle, FaCalendarAlt, FaBolt } from "react-icons/fa";
import SlotDropdown from "../../../components/Products/SlotDropdown";

/**
 * CartDeliveryOptions
 * Dynamically displays all delivery options (Express, Standard, Scheduled, etc.)
 * returned from the backend API, automatically adjusting layout for 1, 2, or 3+ options.
 */
const CartDeliveryOptions = ({
  deliveryOption = "",
  onSelectDeliveryOption,
  selectedTimeSlot = "",
  onSelectTimeSlot,
  deliveryOptions = [],
  deliveryFee = 20,
}) => {
  // Default fallback if backend hasn't returned delivery_options yet
  const defaultOptions = [
    {
      type: "EXPRESS",
      fee: Number(deliveryFee) || 20,
    },
    {
      type: "STANDARD",
      fee: 10,
    },
    {
      type: "SCHEDULED",
      fee: 0,
      slots: [],
    },
  ];

  const optionsToRender =
    Array.isArray(deliveryOptions) && deliveryOptions.length > 0
      ? deliveryOptions
      : defaultOptions;

  const getOptionConfig = (type) => {
    const upper = String(type || "").toUpperCase();
    switch (upper) {
      case "EXPRESS":
        return {
          title: "Express Delivery",
          badge: "⚡ 30 Mins",
          badgeClass: "bg-amber-50 text-amber-700 border-amber-200",
          description: "Delivered in 30 minutes",
          subtitle: "Fastest doorstep delivery",
          icon: FaMotorcycle,
        };
      case "STANDARD":
        return {
          title: "Standard Delivery",
          badge: "📦 Standard",
          badgeClass: "bg-blue-50 text-blue-700 border-blue-200",
          description: "Same day / Next day",
          subtitle: "Regular doorstep delivery",
          icon: FaTruck,
        };
      case "SCHEDULED":
        return {
          title: "Scheduled Delivery",
          badge: "",
          badgeClass: "bg-emerald-50 text-[#046a38] border-emerald-200",
          description: "Choose preferred slot",
          subtitle: "Convenient scheduled delivery",
          icon: FaCalendarAlt,
        };
      case "INSTANT":
      case "FAST":
        return {
          title: "Instant Delivery",
          badge: "⚡ Fast",
          badgeClass: "bg-amber-50 text-amber-700 border-amber-200",
          description: "Direct priority dispatch",
          subtitle: "Quick doorstep delivery",
          icon: FaBolt,
        };
      default:
        return {
          title: `${upper.charAt(0) + upper.slice(1).toLowerCase()} Delivery`,
          badge: "🚚 Delivery",
          badgeClass: "bg-gray-50 text-gray-700 border-gray-200",
          description: "Standard delivery",
          subtitle: "Doorstep delivery",
          icon: FaTruck,
        };
    }
  };

  // Responsive grid class based on number of options
  const gridClass =
    optionsToRender.length >= 3
      ? "grid-cols-1 md:grid-cols-3"
      : optionsToRender.length === 2
      ? "grid-cols-1 md:grid-cols-2"
      : "grid-cols-1";

  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-4 sm:p-5 shadow-sm">
      <div className="mb-3.5 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-emerald-50 text-[#046a38]">
            <FaTruck className="h-4 w-4" />
          </div>
          <h2 className="text-base font-bold text-gray-900">
            Delivery Options
          </h2>
        </div>
        <span className="text-xs font-semibold text-gray-500">
          {optionsToRender.length} {optionsToRender.length === 1 ? "Option" : "Options"} Available
        </span>
      </div>

      <div className={`grid gap-3.5 ${gridClass}`}>
        {optionsToRender.map((opt) => {
          const typeStr = String(opt.type || "").trim();
          const typeUpper = typeStr.toUpperCase();
          const typeLower = typeStr.toLowerCase();
          const isSelected =
            String(deliveryOption || "").trim().toLowerCase() === typeLower;
          const feeNum = Number(opt.fee ?? 0);
          const config = getOptionConfig(typeUpper);
          const Icon = config.icon;
          const slots = Array.isArray(opt.slots) ? opt.slots : [];


          return (
            <div
              key={typeUpper}
              onClick={() => {
                onSelectDeliveryOption(typeLower, feeNum);
                // If this option has slots and none selected, auto-pick first available
                if (slots.length > 0 && !selectedTimeSlot && onSelectTimeSlot) {
                  const firstSlot = slots[0];
                  const firstSlotVal = firstSlot.value || firstSlot.label;
                  const firstSlotId = firstSlot.slot_id != null ? Number(firstSlot.slot_id) : 1;
                  if (firstSlotVal) onSelectTimeSlot(firstSlotVal, firstSlotId);
                }
              }}
              className={`group relative flex flex-col justify-between cursor-pointer rounded-xl border p-4 transition-all duration-200 ${
                isSelected
                  ? "border-2 border-[#046a38] bg-emerald-50/25 shadow-sm ring-1 ring-[#046a38]/20"
                  : "border-gray-200 bg-white hover:border-emerald-300 hover:shadow-xs"
              }`}
            >
              {/* Top Row: Radio + Title + Icon */}
              <div>
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-2.5">
                    {/* Custom Radio Circle */}
                    <div
                      className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-full border transition-colors ${
                        isSelected
                          ? "border-[#046a38] bg-white"
                          : "border-gray-300 group-hover:border-gray-400"
                      }`}
                    >
                      {isSelected && (
                        <div className="h-2 w-2 rounded-full bg-[#046a38]" />
                      )}
                    </div>
                    <div>
                      <h3 className="text-sm font-bold text-gray-900 leading-snug">
                        {config.title}
                      </h3>
                    </div>
                  </div>

                  <div
                    className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-lg ${
                      isSelected
                        ? "bg-[#046a38] text-white shadow-xs"
                        : "bg-gray-100 text-gray-600 group-hover:bg-emerald-50 group-hover:text-[#046a38]"
                    } transition-colors`}
                  >
                    <Icon className="h-3.5 w-3.5" />
                  </div>
                </div>

                {/* Subtitle & Badge */}
                <div className="mt-2 pl-6.5">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span
                      className={`inline-flex items-center rounded-md border px-1.5 py-0.5 text-[10px] font-bold ${config.badgeClass}`}
                    >
                      {config.badge}
                    </span>
                    <span className="text-[11px] text-gray-500">
                      {config.description}
                    </span>
                  </div>
                </div>
              </div>

              {/* Dynamic Time Slot Selector (for options with slots) */}
              {(slots.length > 0 || typeUpper === "SCHEDULED") && (
                <div className="mt-3 pl-6.5" onClick={(e) => e.stopPropagation()}>
                  <SlotDropdown
                    value={selectedTimeSlot}
                    slots={slots}
                    onChange={(slotVal, slotId) => {
                      if (onSelectTimeSlot) onSelectTimeSlot(slotVal, slotId);
                      onSelectDeliveryOption(typeLower, feeNum);
                    }}
                  />
                </div>
              )}

              {/* Bottom Row: Delivery Fee */}
              <div className="mt-3.5 flex items-center justify-between border-t border-gray-100 pt-2.5 pl-6.5">
                <span className="text-xs text-gray-500 font-medium">
                  Delivery Fee:
                </span>
                <span
                  className={`text-xs font-extrabold ${
                    feeNum === 0 ? "text-[#046a38]" : "text-gray-900"
                  }`}
                >
                  {feeNum === 0 ? "FREE" : `₹${feeNum.toFixed(2)}`}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default CartDeliveryOptions;
